import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useTranslation } from "react-i18next";
import SEO from "../components/common/SEO";
import StructuredData from "../components/common/StructuredData";
import FAQSchema from "../components/common/FAQSchema";
import CanonicalLink from "../components/common/CanonicalLink";
import { TRACKS, type Track, type TrackConfig } from "../content/landingTracks";

// ─── Animation Variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const scaleIn = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
};

// ─── Theme ────────────────────────────────────────────────────────────────────

const darkVars = {
  "--lp-bg": "#111111",
  "--lp-bg-alt": "#0D0D0D",
  "--lp-card": "#1C1C1E",
  "--lp-card-border": "rgba(255,255,255,0.08)",
  "--lp-header-scrolled": "rgba(17,17,17,0.95)",
  "--lp-header-border": "rgba(255,255,255,0.06)",
  "--lp-footer-bg": "#111111",
  "--lp-footer-border": "rgba(255,255,255,0.08)",
  "--lp-card-gradient-stop": "rgba(28,28,30,1)",
  "--lp-text": "#ffffff",
  "--lp-text-quote": "#e5e7eb",
  "--lp-text-muted": "#9ca3af",
  "--lp-text-subtle": "#6b7280",
  "--lp-text-faint": "#4b5563",
  "--lp-divider": "#374151",
};

const lightVars = {
  "--lp-bg": "#ffffff",
  "--lp-bg-alt": "#f5f5f7",
  "--lp-card": "#ffffff",
  "--lp-card-border": "rgba(0,0,0,0.09)",
  "--lp-header-scrolled": "rgba(255,255,255,0.95)",
  "--lp-header-border": "rgba(0,0,0,0.08)",
  "--lp-footer-bg": "#f5f5f7",
  "--lp-footer-border": "rgba(0,0,0,0.08)",
  "--lp-card-gradient-stop": "rgba(255,255,255,1)",
  "--lp-text": "#111111",
  "--lp-text-quote": "#1f2937",
  "--lp-text-muted": "#4b5563",
  "--lp-text-subtle": "#6b7280",
  "--lp-text-faint": "#9ca3af",
  "--lp-divider": "#d1d5db",
};

// ─── Star SVG ─────────────────────────────────────────────────────────────────

const Star: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#FB923C" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// ─── Theme Toggle ─────────────────────────────────────────────────────────────

const ThemeToggle: React.FC<{ isDark: boolean; onToggle: () => void }> = ({ isDark, onToggle }) => (
  <motion.button
    onClick={onToggle}
    whileHover={{ scale: 1.08 }}
    whileTap={{ scale: 0.94 }}
    aria-label="Toggle light/dark mode"
    className="w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-200"
    style={{
      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
      color: isDark ? "#e5e7eb" : "#374151",
    }}
  >
    {isDark ? (
      // Sun icon
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
    ) : (
      // Moon icon
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    )}
  </motion.button>
);

// ─── Phone frame styles ───────────────────────────────────────────────────────

const phoneFrame: React.CSSProperties = {
  width: "100%",
  borderRadius: 38,
  background: "#0d0d14",
  border: "2px solid rgba(255,255,255,0.12)",
  boxShadow: "0 0 0 1px rgba(0,0,0,0.4), 0 28px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.07)",
  overflow: "hidden",
  aspectRatio: "9 / 19.5",
  position: "relative",
};

const dynamicIsland: React.CSSProperties = {
  position: "absolute",
  top: 10,
  left: "50%",
  transform: "translateX(-50%)",
  width: 60,
  height: 18,
  background: "#000",
  borderRadius: 999,
  zIndex: 10,
};

// ─── Header ───────────────────────────────────────────────────────────────────

interface HeaderProps { isDark: boolean; onToggle: () => void; config: TrackConfig; }

const Header: React.FC<HeaderProps> = ({ isDark, onToggle, config }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className="fixed w-full z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? "var(--lp-header-scrolled)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? "1px solid var(--lp-header-border)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to={config.path}>
          <img
            src={isDark ? "/logo-light-text.png" : "/logo.png"}
            alt="Nemlingo"
            className="h-14 w-auto transition-all duration-300"
          />
        </Link>
        <div className="flex items-center gap-3">
          {/* Switches the language being learned, not the language of the site.
              Kept visually plain so it reads as navigation rather than as a
              second LanguageSwitcher. */}
          <Link
            to={config.otherTrackPath}
            className="text-sm font-semibold hover:opacity-80 transition-opacity"
            style={{ color: "var(--lp-text-muted)" }}
          >
            {t(`${config.keyPrefix}.crossLink`)}
          </Link>
          <ThemeToggle isDark={isDark} onToggle={onToggle} />
          <a
            href="https://apps.apple.com/dk/app/nemlingo/id6751247509"
            target="_blank"
            rel="noopener noreferrer"
          >
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="hidden md:block bg-sky-400 hover:bg-sky-300 text-black font-bold px-6 py-2.5 rounded-full text-sm transition-colors duration-200"
            >
              {t("nav.download", "Download")}
            </motion.button>
          </a>
        </div>
      </div>
    </header>
  );
};

// ─── Footer ───────────────────────────────────────────────────────────────────

interface FooterProps { isDark: boolean; }

const Footer: React.FC<FooterProps> = ({ isDark }) => {
  const { t } = useTranslation();
  return (
    <footer
      className="border-t py-10"
      style={{
        borderColor: "var(--lp-footer-border)",
        backgroundColor: "var(--lp-footer-bg)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col items-center gap-6">
        <img
          src={isDark ? "/logo-light-text.png" : "/logo.png"}
          alt="Nemlingo"
          className="h-10 w-auto opacity-80 transition-all duration-300"
        />
        <nav className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: "var(--lp-text-subtle)" }}>
          <a href="/about" className="hover:opacity-80 transition-opacity">
            {t("nav.about", "About")}
          </a>
          <a href="/privacy-policy" className="hover:opacity-80 transition-opacity">
            {t("nav.privacy", "Privacy Policy")}
          </a>
          <a href="/terms" className="hover:opacity-80 transition-opacity">
            {t("nav.terms", "Terms of Service")}
          </a>
        </nav>
        <p className="text-xs" style={{ color: "var(--lp-text-faint)" }}>
          &copy; {new Date().getFullYear()} Nemlingo. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

interface LandingPageProps {
  /** Which language the page sells learning. Defaults to the primary market. */
  track?: Track;
}

const LandingPage: React.FC<LandingPageProps> = ({ track = "danish" }) => {
  const { t } = useTranslation();
  const config = TRACKS[track];
  const k = (key: string) => `${config.keyPrefix}.${key}`;

  const [isDark, setIsDark] = useState<boolean>(() => {
    const saved = localStorage.getItem("lp-theme");
    return saved ? saved === "dark" : true;
  });

  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem("lp-theme", next ? "dark" : "light");
      return next;
    });
  };

  const steps = [1, 2, 3].map((n) => ({
    number: String(n),
    title: t(k(`howItWorks.s${n}.title`)),
    description: t(k(`howItWorks.s${n}.description`)),
  }));

  const testimonials = config.showTestimonials
    ? [1, 2, 3].map((n) => ({
        quote: t(k(`testimonials.t${n}.quote`)),
        name: t(k(`testimonials.t${n}.name`)),
        location: t(k(`testimonials.t${n}.location`)),
      }))
    : [];

  const faqItems = [1, 2, 3, 4].map((n) => ({
    question: t(k(`faq.q${n}.question`)),
    answer: t(k(`faq.q${n}.answer`)),
  }));

  const screenshots = [
    { src: "/app-screenshots/1.png", label: t(k("shots.lockScreen")), alt: t(k("shots.lockScreenAlt")), color: "#5bc8f5", bg: "rgba(91,200,245,0.1)", border: "rgba(91,200,245,0.2)" },
    { src: "/app-screenshots/2.png", label: t(k("shots.progress")), alt: t(k("shots.progressAlt")), color: "#f5a623", bg: "rgba(245,166,35,0.1)", border: "rgba(245,166,35,0.2)" },
    { src: "/app-screenshots/3.png", label: t(k("shots.audio")), alt: t(k("shots.audioAlt")), color: "#5bc8f5", bg: "rgba(91,200,245,0.12)", border: "rgba(91,200,245,0.3)" },
    { src: "/app-screenshots/4.png", label: t(k("shots.recall")), alt: t(k("shots.recallAlt")), color: "#4caf7d", bg: "rgba(76,175,125,0.1)", border: "rgba(76,175,125,0.2)" },
  ];

  const themeVars = isDark ? darkVars : lightVars;

  return (
    <div
      style={{
        ...(themeVars as React.CSSProperties),
        backgroundColor: "var(--lp-bg)",
        color: "var(--lp-text)",
        fontFamily: "Nunito, sans-serif",
        transition: "background-color 0.3s, color 0.3s",
      }}
    >
      <CanonicalLink />
      <SEO
        title={t(k("seo.title"))}
        description={t(k("seo.description"))}
        keywords={t(k("seo.keywords"))}
        canonicalUrl={`https://nemlingo.com${config.path === "/" ? "" : config.path}`}
      />
      <StructuredData
        type="WebSite"
        data={{
          name: "Nemlingo",
          url: "https://nemlingo.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://nemlingo.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <StructuredData
        type="Organization"
        data={{
          name: "Nemlingo",
          url: "https://nemlingo.com",
          logo: "https://nemlingo.com/logo512.png",
          description: t(k("seo.orgDescription")),
        }}
      />
      <FAQSchema faqs={faqItems} />

      <Header isDark={isDark} onToggle={toggleTheme} config={config} />

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ paddingTop: "80px" }}
      >
        {/* Background glows */}
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "rgba(251,146,60,0.06)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "rgba(56,189,248,0.06)" }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 w-full">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

            {/* Left — Copy */}
            <motion.div
              className="lg:w-1/2 text-center lg:text-left"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
            >
              <div
                className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase"
                style={{
                  backgroundColor: "rgba(91,200,245,0.1)",
                  color: "#5bc8f5",
                  border: "1px solid rgba(91,200,245,0.28)",
                }}
              >
                <span style={{ fontSize: 16 }}>{t(k("hero.badgeFlag"))}</span>
                {t(k("hero.badge"))}
              </div>

              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6"
                style={{ color: "var(--lp-text)", letterSpacing: "-0.03em" }}
              >
                {t(k("hero.titleLine1"))}
                <br />
                {t(k("hero.titleLine2"))}{" "}
                <span
                  style={{
                    background: "linear-gradient(135deg, #5bc8f5, #a78bfa)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {t(k("hero.titleHighlight"))}
                </span>
              </h1>

              <p
                className="text-lg mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed"
                style={{ color: "var(--lp-text-muted)" }}
              >
                {t(k("hero.lead"))}
              </p>

              {/* Store CTAs */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-8">
                <a
                  href="https://apps.apple.com/dk/app/nemlingo/id6751247509"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center gap-3 font-bold px-7 py-4 rounded-2xl text-base shadow-lg"
                    style={{
                      background: "linear-gradient(135deg, #5bc8f5, #3aa8d8)",
                      color: "#0a0e1a",
                      boxShadow: "0 8px 28px rgba(91,200,245,0.35)",
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    Download on iOS
                  </motion.button>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.nemlingo.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center gap-3 font-bold px-7 py-4 rounded-2xl text-base shadow-lg"
                    style={{
                      background: "linear-gradient(135deg, #3ddc84, #2ab36b)",
                      color: "#0a0e1a",
                      boxShadow: "0 8px 28px rgba(61,220,132,0.35)",
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.28.15.6.24.96.24.52 0 .99-.16 1.38-.44l.03-.02 13.2-7.63-2.9-2.9-12.67 10.75zm-2.35-22.43C.32 1.84 0 2.58 0 3.47v17.06c0 .89.32 1.63.84 2.14l.11.1 9.56-9.56v-.23L.94 1.22l-.11.11zm19.31 9.04-2.75-1.59-3.24 3.24 3.24 3.24 2.78-1.6c.79-.46.79-1.83-.03-2.29zM3.18.24 16.38 7.87l-2.9 2.9L.83 1.22C1.22.93 1.69.76 2.22.76c.35 0 .68.09.96.24z"/></svg>
                    Download on Android
                  </motion.button>
                </a>
              </div>

            </motion.div>

            {/* Right — Mascot animation */}
            <motion.div
              className="lg:w-1/2 flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative w-full max-w-md">
                <div
                  className="absolute inset-0 rounded-full blur-3xl pointer-events-none"
                  style={{
                    backgroundColor: "rgba(251,146,60,0.12)",
                    transform: "scale(1.5)",
                  }}
                />
                <DotLottieReact
                  src="/mascot-animation.lottie"
                  loop
                  autoplay
                  style={{ width: "150%", height: "auto", marginLeft: "-25%" }}
                />
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── App Screenshots ─────────────────────────────────────────────────── */}
      <section className="py-24" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Section heading */}
          <motion.div
            className="text-center mb-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p
              className="text-xs font-bold tracking-widest uppercase mb-3"
              style={{ color: "#5bc8f5" }}
            >
              {t(k("features.eyebrow"))}
            </p>
            <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ color: "var(--lp-text)", letterSpacing: "-0.025em" }}>
              {t(k("features.headingBefore"))}{" "}
              <span style={{ color: "#5bc8f5" }}>{t(k("features.headingHighlight"))}</span>
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: "var(--lp-text-muted)" }}>
              {t(k("features.subtext"))}
            </p>
          </motion.div>

          {/* ── Mobile layout: vertical stack ── */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={stagger}
            className="flex flex-col items-center gap-10 sm:hidden"
          >
            {screenshots.map((phone) => (
              <motion.div key={phone.src} variants={scaleIn} className="flex flex-col items-center gap-4" style={{ width: 220 }}>
                <div style={{ ...phoneFrame, borderRadius: 40 }}>
                  <div style={dynamicIsland} />
                  <img src={phone.src} alt={phone.alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <span style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: phone.color,
                  background: phone.bg,
                  border: `1px solid ${phone.border}`,
                  borderRadius: 999,
                  padding: "5px 14px",
                }}>
                  {phone.label}
                </span>
              </motion.div>
            ))}
          </motion.div>

          {/* Asymmetric phone cluster — desktop only */}
          <div className="hidden sm:block">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="relative flex items-end justify-center gap-4 md:gap-6"
            style={{ minHeight: 520, paddingBottom: 40 }}
          >
            {/* Phone 1 — lock screen — far left, rotated, pulled down */}
            <motion.div
              variants={scaleIn}
              className="relative flex-shrink-0 hidden sm:block"
              style={{ transform: "rotate(-9deg) translateY(60px)", width: 145, zIndex: 2 }}
            >
              <div style={phoneFrame}>
                <div style={dynamicIsland} />
                <img src="/app-screenshots/1.png" alt={t(k("shots.lockScreenAlt"))} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <motion.div
                style={{
                  position: "absolute",
                  bottom: -36,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#5bc8f5",
                  background: "rgba(91,200,245,0.1)",
                  border: "1px solid rgba(91,200,245,0.2)",
                  borderRadius: 999,
                  padding: "4px 10px",
                }}
              >
                {t(k("shots.lockScreen"))}
              </motion.div>
            </motion.div>

            {/* Phone 2 — home / dashboard — left-center, slight tilt */}
            <motion.div
              variants={scaleIn}
              className="relative flex-shrink-0"
              style={{ transform: "rotate(-4deg) translateY(20px)", width: 175, zIndex: 3 }}
            >
              <div style={{ ...phoneFrame, borderRadius: 40 }}>
                <div style={dynamicIsland} />
                <img src="/app-screenshots/2.png" alt={t(k("shots.progressAlt"))} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              {/* glow */}
              <div style={{ position: "absolute", bottom: -16, left: "50%", transform: "translateX(-50%)", width: "70%", height: 28, borderRadius: "50%", background: "rgba(245,166,35,0.5)", filter: "blur(14px)", opacity: 0.7 }} />
              <motion.div
                style={{
                  position: "absolute",
                  bottom: -36,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#f5a623",
                  background: "rgba(245,166,35,0.1)",
                  border: "1px solid rgba(245,166,35,0.2)",
                  borderRadius: 999,
                  padding: "4px 10px",
                }}
              >
                {t(k("shots.progress"))}
              </motion.div>
            </motion.div>

            {/* Phone 3 — flashcard hero — center, straight, largest */}
            <motion.div
              variants={scaleIn}
              className="relative flex-shrink-0"
              style={{ transform: "translateY(-10px)", width: 210, zIndex: 5 }}
            >
              <div style={{ ...phoneFrame, borderRadius: 44, border: "2.5px solid rgba(91,200,245,0.35)" }}>
                <div style={dynamicIsland} />
                <img src="/app-screenshots/3.png" alt={t(k("shots.audioAlt"))} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              {/* strong glow */}
              <div style={{ position: "absolute", bottom: -20, left: "50%", transform: "translateX(-50%)", width: "80%", height: 36, borderRadius: "50%", background: "rgba(91,200,245,0.55)", filter: "blur(18px)", opacity: 0.8 }} />
              <motion.div
                style={{
                  position: "absolute",
                  bottom: -40,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#5bc8f5",
                  background: "rgba(91,200,245,0.12)",
                  border: "1px solid rgba(91,200,245,0.3)",
                  borderRadius: 999,
                  padding: "5px 12px",
                }}
              >
                {t(k("shots.audio"))}
              </motion.div>
            </motion.div>

            {/* Phone 4 — answer / spaced rep — right-center, tilt right */}
            <motion.div
              variants={scaleIn}
              className="relative flex-shrink-0"
              style={{ transform: "rotate(4deg) translateY(20px)", width: 175, zIndex: 3 }}
            >
              <div style={{ ...phoneFrame, borderRadius: 40 }}>
                <div style={dynamicIsland} />
                <img src="/app-screenshots/4.png" alt={t(k("shots.recallAlt"))} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              {/* glow */}
              <div style={{ position: "absolute", bottom: -16, left: "50%", transform: "translateX(-50%)", width: "70%", height: 28, borderRadius: "50%", background: "rgba(76,175,125,0.5)", filter: "blur(14px)", opacity: 0.7 }} />
              <motion.div
                style={{
                  position: "absolute",
                  bottom: -36,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#4caf7d",
                  background: "rgba(76,175,125,0.1)",
                  border: "1px solid rgba(76,175,125,0.2)",
                  borderRadius: 999,
                  padding: "4px 10px",
                }}
              >
                {t(k("shots.recall"))}
              </motion.div>
            </motion.div>

            {/* Phone 5 duplicate of 1 for far right on large screens — lock screen again, opposite tilt */}
            <motion.div
              variants={scaleIn}
              className="relative flex-shrink-0 hidden lg:block"
              style={{ transform: "rotate(9deg) translateY(60px)", width: 145, zIndex: 2 }}
            >
              <div style={phoneFrame}>
                <div style={dynamicIsland} />
                <img src="/app-screenshots/1.png" alt={t(k("shots.lockScreenAlt"))} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <motion.div
                style={{
                  position: "absolute",
                  bottom: -36,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#a78bfa",
                  background: "rgba(167,139,250,0.1)",
                  border: "1px solid rgba(167,139,250,0.2)",
                  borderRadius: 999,
                  padding: "4px 10px",
                }}
              >
                {t(k("shots.daily"))}
              </motion.div>
            </motion.div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────────────────────── */}
      <section
        className="py-24"
        style={{ backgroundColor: "var(--lp-bg-alt)" }}
        id="how-it-works"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ color: "var(--lp-text)" }}>
              {t(k("howItWorks.headingBefore"))}{" "}
              <span style={{ color: "#38BDF8" }}>{t(k("howItWorks.headingHighlight"))}</span>
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: "var(--lp-text-muted)" }}>
              {t(k("howItWorks.subtext"))}
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {steps.map((step, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                whileHover={{ scale: 1.03, transition: { duration: 0.25 } }}
                className="rounded-2xl p-8 text-center"
                style={{
                  backgroundColor: "var(--lp-card)",
                  border: "1px solid var(--lp-card-border)",
                }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-6"
                  style={{ backgroundColor: "#FB923C", color: "#111111" }}
                >
                  {step.number}
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "var(--lp-text)" }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--lp-text-muted)" }}>{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Testimonials ────────────────────────────────────────────────────── */}
      {config.showTestimonials && (
      <section className="py-24" id="testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ color: "var(--lp-text)" }}>
              {t(k("testimonials.headingBefore"))}{" "}
              <span style={{ color: "#FB923C" }}>{t(k("testimonials.headingHighlight"))}</span>
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {testimonials.map((item, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                className="rounded-2xl p-7"
                style={{
                  backgroundColor: "var(--lp-card)",
                  border: "1px solid var(--lp-card-border)",
                }}
              >
                <div className="flex gap-0.5 mb-5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} />
                  ))}
                </div>
                <p className="italic mb-5 leading-relaxed" style={{ color: "var(--lp-text-quote)" }}>
                  &ldquo;{item.quote}&rdquo;
                </p>
                <p className="text-sm font-semibold" style={{ color: "var(--lp-text-subtle)" }}>
                  — {item.name}, {item.location}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      )}

      {/* ── Final CTA ───────────────────────────────────────────────────────── */}
      <section
        className="py-28 relative overflow-hidden"
        style={{ backgroundColor: "var(--lp-bg-alt)" }}
        id="cta"
      >
        {/* Glows */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "rgba(56,189,248,0.07)" }}
        />
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "rgba(251,146,60,0.07)" }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
              style={{ color: "var(--lp-text)" }}
            >
              {t(k("cta.headingBefore"))}{" "}
              <span style={{ color: "#38BDF8" }}>{t(k("cta.headingHighlight"))}</span>
            </h2>
            <p className="text-lg mb-10 max-w-xl mx-auto" style={{ color: "var(--lp-text-muted)" }}>
              {t(k("cta.subtext"))}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
              <a
                href="https://apps.apple.com/dk/app/nemlingo/id6751247509"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-3 font-bold px-8 py-4 rounded-2xl text-lg"
                  style={{
                    background: "linear-gradient(135deg, #5bc8f5, #3aa8d8)",
                    color: "#0a0e1a",
                    boxShadow: "0 8px 32px rgba(91,200,245,0.35)",
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  Download on iOS
                </motion.button>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.nemlingo.app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-3 font-bold px-8 py-4 rounded-2xl text-lg"
                  style={{
                    background: "linear-gradient(135deg, #3ddc84, #2ab36b)",
                    color: "#0a0e1a",
                    boxShadow: "0 8px 32px rgba(61,220,132,0.35)",
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.28.15.6.24.96.24.52 0 .99-.16 1.38-.44l.03-.02 13.2-7.63-2.9-2.9-12.67 10.75zm-2.35-22.43C.32 1.84 0 2.58 0 3.47v17.06c0 .89.32 1.63.84 2.14l.11.1 9.56-9.56v-.23L.94 1.22l-.11.11zm19.31 9.04-2.75-1.59-3.24 3.24 3.24 3.24 2.78-1.6c.79-.46.79-1.83-.03-2.29zM3.18.24 16.38 7.87l-2.9 2.9L.83 1.22C1.22.93 1.69.76 2.22.76c.35 0 .68.09.96.24z"/></svg>
                  Download on Android
                </motion.button>
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-sm" style={{ color: "var(--lp-text-subtle)" }}>
              <span>{t(k("cta.pill1"))}</span>
              <span className="w-px h-4 inline-block self-center" style={{ backgroundColor: "var(--lp-divider)" }} />
              <span>{t(k("cta.pill2"))}</span>
              <span className="w-px h-4 inline-block self-center" style={{ backgroundColor: "var(--lp-divider)" }} />
              <span>{t(k("cta.pill3"))}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default LandingPage;
